import React, { forwardRef, useCallback, useRef, useLayoutEffect, useState } from 'react'
import classNames from 'classnames'
import ContentEditable from 'react-contenteditable'
import * as linkify from 'linkifyjs'
import InputContainer from './InputContainer'
import sanitizeHtml from 'sanitize-html'

import './Input.scss'

interface Props extends React.InputHTMLAttributes<HTMLInputElement>,
  React.ComponentProps<typeof InputContainer> {
  allowLastPass?: boolean
  inputClassName?: string
  onFieldInvalid?: (errorMessage: string | undefined) => void
}

export const Textbox = forwardRef<HTMLInputElement, Props>(({
  inputClassName,
  error,
  block,
  large,
  small,
  onChange,
  name,
  id,
  onFieldInvalid,
  ...rest
}, ref) => {
  const [value, setValue] = useState(String(rest.value))
  const inputRef = useRef<HTMLInputElement>(null)

  const onTextBoxPaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {

    const data = e.clipboardData.getData('text/html')
    
    const html = sanitizeHtml(data, {
      allowedTags: ['div', 'br', 'li', 'ol', 'ul', 'p', 'b'],
      allowedAttributes: {}
    })

    if (html.length) {
      e.preventDefault()
      document.execCommand('insertHTML', false, html)
    } 
  }, [])

  const onTextBoxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)

    const html = sanitizeHtml(e.target.value, {
      allowedTags: ['div', 'br', 'li', 'ol', 'ul', 'p', 'b'],
      allowedAttributes: {}
    })

    const links = linkify.find(e.target.value)

    e.target.value = html
    onChange && onChange(e)

    if (inputRef.current) {
      onFieldInvalid && onFieldInvalid(links.length > 0 ? 'URLs are not allowed in the text' : undefined)
    }
  }, [onChange, onFieldInvalid])

  useLayoutEffect(() => {
    if (typeof ref === 'function') {
      ref(inputRef.current)
    }
  }, [ref])

  return <>
    <ContentEditable
      role='textbox'
      className={classNames('input', inputClassName, {
        'invalid': error,
        block,
        large,
        small
      })}
      html={value}
      {...rest}
      onChange={onTextBoxChange as any}
      onPaste={onTextBoxPaste}
    />
    <input
      {...rest}
      type='hidden'
      id={id}
      name={name}
      ref={inputRef}
      value={rest.value}
    />
  </>
})